import {
	Button,
	CategorySelect,
	Checkbox,
	ChipConfirmModal,
	ChoiceChip,
	EmptyState,
	Expandable,
	ExpandableContent,
	ExpandableTrigger,
	Input,
	Label,
	NativeSelect,
	Textarea,
} from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown as CollapseChevron } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { sourceIdentity } from "~/common/article/sources";
import { useAccess, whyDisabled } from "~/core/access/index";
import { ApiError, api, type DatapointField, type Settings } from "~/core/api/index";
import { pick, useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { useCategoryLabels } from "~/core/ui/category-labels";
import { Hint, Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { ModelPicker } from "~/modules/analytics/components/model-picker";
import { ENGINES } from "~/modules/analytics/lib/engines";
import { InsightFiling } from "~/modules/analytics/pages/insights/settings";
import { BrandVoice } from "~/modules/analytics/pages/insights/voice";
import { AudienceSettings } from "./insights/audience-settings";

/**
 * What this screen recommends a brand carries. Not a floor: a brand with fewer
 * still gets pages, and they can state only what its facts say. Two counts,
 * because a page cites sources rather than facts, and three facts from one page
 * are one source.
 */
const MIN_DATAPOINTS = 3;
const MIN_SOURCES = 2;

/** The five questions the brand's own settings answer. */
type BrandSection = "identity" | "facts" | "measurement" | "audiences" | "filing" | "voice";

/**
 * Everything about one brand that is set once and revisited.
 *
 * It was one column, four screens tall, with a single save bar at the foot of
 * it: a person who came to fix a tagline scrolled past the fact table, the
 * engine chips and the model picker, and the bar told them there were unsaved
 * changes without saying which of the eleven fields held them. Worse, one save
 * sent all of it, so a stale tab reopened an hour later wrote its stale
 * measurement settings back over somebody else's.
 *
 * Each section is now one decision with its own save, and the route patches
 * only the fields that section owns — the settings route has always taken a
 * partial body, so this is the shape the server already expected. The filing
 * and the voice sections were already saving section by section; they keep
 * doing that and have no bar.
 *
 * The five are sections of the workspace's settings page rather than tabs of a
 * screen of their own, and this component is what that page mounts for each.
 */
export function BrandSettings({ entityId, section }: { entityId: string; section: BrandSection }) {
	if (section === "audiences")
		return (
			<ReadOnlyForViewers>
				<AudienceSettings entityId={entityId} />
			</ReadOnlyForViewers>
		);
	if (section === "filing")
		// The filing that decides how the detection screens read: how a cited
		// domain is categorised, what the answers call this brand, who it is
		// measured against. Not a reading and not a fact about the brand — but
		// the same shelf, because it is set once and revisited when a number
		// looks wrong.
		return (
			<ReadOnlyForViewers>
				<InsightFiling entityId={entityId} />
			</ReadOnlyForViewers>
		);
	if (section === "voice")
		// The question about writing rather than about reading: what should a
		// page under this brand's name sound like.
		return (
			<ReadOnlyForViewers>
				<BrandVoice entityId={entityId} />
			</ReadOnlyForViewers>
		);
	return <BrandForm entityId={entityId} section={section} />;
}

/**
 * The three sections that are three views of one row.
 *
 * One request behind them, and it is reloaded on a save: fetching per section
 * would let two of them disagree about the brand while somebody clicked
 * between them.
 */
function BrandForm({ entityId, section }: { entityId: string; section: "identity" | "facts" | "measurement" }) {
	const { locale } = useI18n();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "settings"],
		queryFn: () => api.settings(entityId),
		...read,
	});

	return (
		<Async state={state}>
			{(data) => {
				const props = { entityId, data, localeForErrors: locale };
				if (section === "facts") return <Facts {...props} />;
				if (section === "measurement") return <Measurement {...props} />;
				return <Identity {...props} />;
			}}
		</Async>
	);
}

/**
 * A tab that saves section by section, under the same read-only rule as the
 * form tabs.
 *
 * One `<fieldset disabled>` around the lot rather than a `disabled` on each of
 * a dozen inputs: a viewer sees everything and can change none of it, and the
 * routes refuse anyway — that is the boundary. A control that always fails is a
 * bug report.
 */
function ReadOnlyForViewers({ children }: { children: ReactNode }) {
	const { editor, why } = useAccess();
	return (
		<fieldset className="bare-fieldset" disabled={!editor} title={whyDisabled(editor, why.editor)}>
			{children}
		</fieldset>
	);
}

interface TabProps {
	entityId: string;
	data: Settings;
	localeForErrors: "en" | "zh";
}

/**
 * What one tab holds, against what it was loaded with.
 *
 * A form that cannot say whether it has been changed cannot warn anybody about
 * leaving it, and its save button is a button nobody can tell the state of.
 * `initial` has to be memoized by its caller — it is the identity of the load,
 * and a new object every render would reset the fields under the cursor.
 */
function useTabState<T>(initial: T) {
	const [value, setValue] = useState(initial);
	useEffect(() => setValue(initial), [initial]);
	const dirty = JSON.stringify(value) !== JSON.stringify(initial);
	return { value, setValue, dirty, revert: () => setValue(initial) };
}

/**
 * The shell every form tab wears: the read-only fieldset, the save bar, and
 * the one PATCH that tab is allowed to send.
 *
 * `body` is a function of the tab's own fields and nothing else. That is the
 * point of the split — a tab that sent the whole settings object would put
 * back whatever it happened to be holding for the other four.
 */
function TabForm({
	entityId,
	dirty,
	revert,
	body,
	localeForErrors,
	children,
	wide = false,
}: {
	entityId: string;
	dirty: boolean;
	revert: () => void;
	body: () => Record<string, unknown>;
	localeForErrors: "en" | "zh";
	children: ReactNode;
	wide?: boolean;
}) {
	const t = useT();
	const { editor, why } = useAccess();
	const [busy, setBusy] = useState(false);
	const queryClient = useQueryClient();
	// The whole brand, not the settings alone: the competitor lists, the
	// engines and the facts are read by other screens too.
	const write = useMutation({
		mutationFn: (patch: Record<string, unknown>) => api.saveSettings(entityId, patch),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
			await queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes("brands") });
		},
	});

	// A tab closed mid-edit. The browser's own dialog, because this is the one
	// case a page cannot handle itself.
	useEffect(() => {
		if (!dirty) return;
		const warn = (event: BeforeUnloadEvent) => event.preventDefault();
		window.addEventListener("beforeunload", warn);
		return () => window.removeEventListener("beforeunload", warn);
	}, [dirty]);

	async function save(event: React.FormEvent) {
		event.preventDefault();
		setBusy(true);
		try {
			await write.mutateAsync(body());
			toast.success(t("settings.saved"));
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(localeForErrors) : String(e));
		} finally {
			setBusy(false);
		}
	}

	return (
		<form onSubmit={save} className={wide ? "settings-form settings-form-wide" : "settings-form"}>
			{/* The whole tab in one fieldset: "none of this is yours to change"
			    said once rather than on every input, and no field that accepts a
			    change nothing will keep. */}
			<fieldset disabled={!editor} className="bare-fieldset">
				{children}
			</fieldset>

			{/* The bar stays on the screen, and it carries the one thing the old
			    screen never said per decision: whether *this* tab has anything to
			    save. */}
			<div className="form-bar">
				<Button type="submit" disabled={busy || !editor || !dirty} title={whyDisabled(editor, why.editor)}>
					{busy ? t("login.working") : t("settings.save")}
				</Button>
				{dirty ? (
					<>
						<span className="hint">{t("settings.unsaved")}</span>
						<Button type="button" variant="link" onClick={revert}>
							{t("settings.revert")}
						</Button>
					</>
				) : (
					<span className="hint">{t("settings.allSaved")}</span>
				)}
			</div>
		</form>
	);
}

// ---------------------------------------------------------------------------
// Who this brand is
// ---------------------------------------------------------------------------

function identityOf(data: Settings) {
	return {
		logoImage: "",
		name: data.entity?.name ?? "",
		website: data.entity?.website ?? "",
		sourcePages: (data.entity?.source_pages ?? []).join("\n"),
		categories: data.entity?.categories ?? [],
		aliases: (data.entity?.aliases ?? []).join("\n"),
		domains: (data.entity?.domains ?? []).join("\n"),
		taglineEn: data.entity?.tagline_en ?? "",
		taglineZh: data.entity?.tagline_zh ?? "",
	};
}

function Identity({ entityId, data, localeForErrors }: TabProps) {
	const t = useT();
	const { locale } = useI18n();
	const categoryLabels = useCategoryLabels(t("settings.categories"));
	const { admin } = useAccess();
	const initial = useMemo(() => identityOf(data), [data]);
	const { value, setValue, dirty, revert } = useTabState(initial);
	const set = (patch: Partial<ReturnType<typeof identityOf>>) => setValue((current) => ({ ...current, ...patch }));

	return (
		<>
			<TabForm
				entityId={entityId}
				dirty={dirty}
				revert={revert}
				localeForErrors={localeForErrors}
				body={() => ({
					...(value.logoImage ? { logo_image: value.logoImage } : {}),
					name: value.name.trim(),
					website: value.website.trim(),
					source_pages: lines(value.sourcePages),
					categories: value.categories,
					aliases: lines(value.aliases),
					domains: lines(value.domains),
					tagline_en: value.taglineEn || null,
					tagline_zh: value.taglineZh || null,
				})}
			>
				<Section title={t("settings.identity")} lede={t("settings.identityHint")}>
					<div className="field">
						<Label htmlFor="brand-logo">{t("settings.logo")}</Label>
						{value.logoImage || data.entity?.logo_url ? (
							<img
								src={value.logoImage || data.entity?.logo_url || ""}
								alt=""
								className="size-(--s-14) rounded-control object-contain"
							/>
						) : null}
						<Input
							id="brand-logo"
							type="file"
							accept="image/png,image/jpeg,image/webp,image/gif"
							onChange={async (event) => {
								const file = event.target.files?.[0];
								event.target.value = "";
								if (!file) return;
								try {
									set({ logoImage: await compressLogo(file) });
								} catch {
									toast.error(t("settings.logoInvalid"));
								}
							}}
						/>
						<p className="hint">{t("settings.logoHint")}</p>
					</div>
					<div className="field-pair">
						<div className="field">
							<div className="title-row">
								<Label htmlFor="brand-name">{t("settings.brandName")}</Label>
								<Hint text={t("settings.brandNameHint")} />
							</div>
							<Input
								id="brand-name"
								required
								minLength={2}
								maxLength={80}
								value={value.name}
								onChange={(e) => set({ name: e.target.value })}
							/>
						</div>
						<div className="field">
							<div className="title-row">
								<Label htmlFor="brand-website">{t("settings.website")}</Label>
								<Hint text={t("settings.websiteHint")} />
							</div>
							<Input
								id="brand-website"
								type="url"
								placeholder="https://example.com"
								value={value.website}
								onChange={(e) => set({ website: e.target.value })}
							/>
						</div>
					</div>

					<div className="field">
						<div className="title-row">
							<Label htmlFor="brand-source-pages">{t("settings.sourcePages")}</Label>
							<Hint text={t("settings.sourcePagesHint")} />
						</div>
						<Textarea
							id="brand-source-pages"
							className="font-code"
							rows={3}
							placeholder="https://detail.1688.com/offer/…"
							value={value.sourcePages}
							onChange={(e) => set({ sourcePages: e.target.value })}
						/>
					</div>

					<div className="field">
						<div className="title-row">
							<Label htmlFor="brand-categories">{t("settings.categories")}</Label>
							<Hint text={t("settings.categoriesHint")} />
						</div>
						<CategorySelect
							id="brand-categories"
							value={value.categories}
							onChange={(categories) => set({ categories })}
							locale={locale}
							labels={categoryLabels}
						/>
					</div>

					<div className="field">
						<div className="title-row">
							<Label htmlFor="aliases">{t("settings.aliases")}</Label>
							<Hint text={t("settings.aliasesHint")} />
						</div>
						<Textarea
							id="aliases"
							className="font-code"
							rows={3}
							value={value.aliases}
							onChange={(e) => set({ aliases: e.target.value })}
						/>
					</div>

					<div className="field">
						<div className="title-row">
							<Label htmlFor="domains">{t("settings.domains")}</Label>
							<Hint text={t("settings.domainsHint")} />
						</div>
						<Textarea
							id="domains"
							className="font-code"
							rows={3}
							value={value.domains}
							onChange={(e) => set({ domains: e.target.value })}
						/>
					</div>

					<div className="field-pair">
						<div className="field">
							<Label htmlFor="tagline-en">{t("settings.taglineEn")}</Label>
							<Input
								id="tagline-en"
								value={value.taglineEn}
								onChange={(e) => set({ taglineEn: e.target.value })}
								maxLength={160}
							/>
							<span className="hint">{t("settings.taglineCount", { n: value.taglineEn.length })}</span>
						</div>
						<div className="field">
							<Label htmlFor="tagline-zh">{t("settings.taglineZh")}</Label>
							<Input
								id="tagline-zh"
								value={value.taglineZh}
								onChange={(e) => set({ taglineZh: e.target.value })}
								maxLength={160}
							/>
							<span className="hint">{t("settings.taglineCount", { n: value.taglineZh.length })}</span>
						</div>
					</div>
				</Section>
			</TabForm>

			{/* Removing a brand is an account decision, not a brand one — an
			    editor of it is not the person who decides it stops being tracked.
			    Outside the form: it is not a setting, and a destructive button
			    inside a form is one stray Enter away from being pressed. It sits
			    on this tab because deleting the brand is the last thing on the
			    list of what the brand *is*, not of how it is measured. */}
			{admin ? (
				<DangerZone entityId={entityId} name={data.entity?.name ?? ""} localeForErrors={localeForErrors} />
			) : null}
		</>
	);
}

// ---------------------------------------------------------------------------
// What is true about it
// ---------------------------------------------------------------------------

/**
 * Facts, not settings — but this is where their owner is, and it is the most
 * consequential tab on the screen: a page can say only what these facts say, so
 * the count says where it stands before any of the rows do.
 *
 * Every fact the industry defines gets a row, filled or not. The empty ones are
 * the work, and a screen that showed only what was already there could not be
 * used to do it.
 */
function Facts({ entityId, data, localeForErrors }: TabProps) {
	const t = useT();
	const initial = useMemo(() => toPoints(data.entity?.datapoints, data.entity?.datapoint_schema), [data]);
	const { value: points, setValue: setPoints, dirty, revert } = useTabState(initial);

	const { editor } = useAccess();
	const [collecting, setCollecting] = useState(false);
	const queryClient = useQueryClient();
	const gather = useMutation({
		mutationFn: () => api.collectFacts(entityId),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});
	async function collect() {
		setCollecting(true);
		try {
			const result = await gather.mutateAsync();
			toast.success(t("settings.collectedFacts", { n: result.added }));
		} catch (error) {
			toast.error(error instanceof ApiError ? error.localized(localeForErrors) : String(error));
		} finally {
			setCollecting(false);
		}
	}

	const filled = points.filter((p) => p.value.trim() !== "").length;
	const sources = new Set(
		[
			...points.filter((p) => p.value.trim() !== "" && p.source_url.trim() !== "").map((p) => p.source_url.trim()),
			...(data.entity?.website ? [data.entity.website] : []),
		]
			.map(sourceIdentity)
			.filter(Boolean),
	).size;
	const enough = filled >= MIN_DATAPOINTS && sources >= MIN_SOURCES;

	return (
		<TabForm
			entityId={entityId}
			dirty={dirty}
			revert={revert}
			localeForErrors={localeForErrors}
			body={() => ({ datapoints: fromPoints(points) })}
			wide
		>
			<Section title={t("settings.datapoints")} lede={t("settings.datapointsHint")}>
				<div className="row-actions">
					<Button type="button" variant="default" disabled={!editor || collecting || dirty} onClick={collect}>
						{collecting ? t("settings.collectingFacts") : t("settings.collectFacts")}
					</Button>
					<span className="hint">{t("settings.collectFactsHint")}</span>
				</div>
				<p className={enough ? "hint" : "banner banner-note"}>
					{enough
						? t("settings.datapointsEnough", { n: filled, sources })
						: t("settings.datapointsShort", {
								n: filled,
								min: MIN_DATAPOINTS,
								sources,
								minSources: MIN_SOURCES,
							})}
				</p>
				{points.length === 0 ? (
					<EmptyState size="compact" title={t("settings.datapointsNone")} />
				) : (
					<div className="stack">
						<div className="datapoints">
							{points.map((point, index) =>
								initial[index]?.value.trim() ? (
									<DatapointRow
										key={point.key}
										point={point}
										field={data.entity?.datapoint_schema?.[point.key]}
										onChange={(patch) => setPoints((current) => replace(current, index, patch))}
									/>
								) : null,
							)}
						</div>
						<Expandable key={String(editor)} defaultOpen={!editor}>
							<ExpandableTrigger asChild>
								<Button
									variant="ghost"
									type="button"
									className="group w-full h-auto min-h-(--control-h) justify-start whitespace-normal text-left"
								>
									{t("settings.missingFacts", { n: initial.filter((p) => !p.value.trim()).length })}
									<CollapseChevron
										aria-hidden
										className="ml-auto shrink-0 transition-transform group-data-[state=open]:rotate-180"
									/>
								</Button>
							</ExpandableTrigger>
							<ExpandableContent>
								<div className="datapoints">
									{points.map((point, index) =>
										!initial[index]?.value.trim() ? (
											<DatapointRow
												key={point.key}
												point={point}
												field={data.entity?.datapoint_schema?.[point.key]}
												onChange={(patch) => setPoints((current) => replace(current, index, patch))}
											/>
										) : null,
									)}
								</div>
							</ExpandableContent>
						</Expandable>
					</div>
				)}
			</Section>
		</TabForm>
	);
}

// ---------------------------------------------------------------------------
// How it is measured
// ---------------------------------------------------------------------------

function measurementOf(data: Settings) {
	return {
		// This brand's switch, not the account's. The account-wide one lives on
		// the run panel, beside the spend it stops.
		paused: Boolean(data.entity?.paused),
		// Empty is "follow the default", which is what the placeholder option
		// says. Storing the current default instead would freeze this account
		// on it the day the default moves.
		researchModel: data.org?.research_model ?? "",
		// What the brand is measured on, not what it once asked for. The wizard
		// runs before a plan is chosen and stores every engine picked there, so
		// a starter account arrives here with eleven of them; drawing those as
		// the selection tells the customer they have what they are not paying
		// for, and sends back a list the server refuses.
		engines: data.entity?.engines_effective ?? [],
	};
}

function Measurement({ entityId, data, localeForErrors }: TabProps) {
	const t = useT();
	const initial = useMemo(() => measurementOf(data), [data]);
	const { value, setValue, dirty, revert } = useTabState(initial);
	const set = (patch: Partial<ReturnType<typeof measurementOf>>) => setValue((current) => ({ ...current, ...patch }));
	const enginesMax = data.org?.engines_max ?? 0;
	const enginesChanged = value.engines.join() !== initial.engines.join();

	// The board, for the model picker. Its own request because it is the one
	// thing on this form that comes from outside ondit — if asale is
	// unreachable, the rest of the settings still save.
	const catalogue = useQuery({
		queryKey: [...keys.entity(entityId), "research-models"],
		queryFn: () => api.researchModels(entityId),
		...read,
	});

	function toggleEngine(key: string) {
		setValue((current) => ({
			...current,
			engines: current.engines.includes(key)
				? current.engines.filter((engine) => engine !== key)
				: current.engines.length < enginesMax
					? [...current.engines, key]
					: current.engines,
		}));
	}

	return (
		<TabForm
			entityId={entityId}
			dirty={dirty}
			revert={revert}
			localeForErrors={localeForErrors}
			body={() => ({
				paused: value.paused,
				research_model: value.researchModel || null,
				// Only when the picker was actually touched: sending the cut
				// list back on an unrelated edit would overwrite a choice made
				// under a bigger plan, which the account gets back when it moves
				// up again. An empty list would be refused, and "no choice" is a
				// state the server keeps.
				...(enginesChanged && value.engines.length > 0 ? { engines: value.engines } : {}),
			})}
		>
			<Section title={t("settings.measurement")} lede={t("settings.measurementHint")}>
				{/*
				 * Which assistants ask about this brand. Chips past the plan's
				 * count stay visible and dimmed: the plan is why they cannot be
				 * picked, and a list that hides them hides the reason.
				 */}
				<fieldset className="field bare-fieldset">
					<legend>{t("settings.engines")}</legend>
					<span className="hint">
						{t("settings.enginesChosen", { n: value.engines.length, max: enginesMax })}
						{(data.entity?.engines ?? []).length === 0 ? ` · ${t("settings.enginesDefault")}` : ""}
					</span>
					<EnginePicker chosen={value.engines} max={enginesMax} onToggle={toggleEngine} />
				</fieldset>

				{/*
				 * The one model an account may change, and the reason it may is
				 * that nothing this one writes is published. What writes a
				 * directory page is fixed — the directory has one voice across
				 * every page on it, and that is not a per-account setting.
				 */}
				<ModelPicker
					value={value.researchModel}
					onChange={(researchModel) => set({ researchModel })}
					defaultModel={catalogue.data?.default ?? data.org?.research_model_default ?? ""}
					catalogue={catalogue}
				/>

				<div className="field">
					<Label htmlFor="paused">
						<Checkbox
							id="paused"
							checked={value.paused}
							onCheckedChange={(checked) => set({ paused: checked === true })}
						/>
						{t("settings.pause")}
					</Label>
					<span className="hint">{t("settings.pauseHint")}</span>
				</div>
			</Section>
		</TabForm>
	);
}

/**
 * One fact: what it is, what it says, and where that was read.
 *
 * The control follows the schema's type rather than being a text box for
 * everything — a boolean typed as the word "true" is a string, and it sorts
 * next to "tremendous" in a comparison table. The source is beside the value
 * and not behind a disclosure, because a value without one is not saved at
 * all, and a form that hides the required half teaches people that the rule is
 * an obstacle.
 */
function DatapointRow({
	point,
	field,
	onChange,
}: {
	point: Point;
	field?: DatapointField;
	onChange: (patch: Partial<Point>) => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const label = (field && pick(locale, [field.label_en, field.label_zh])) || point.key;
	const unit = field ? pick(locale, [field.unit, field.unit_zh]) : undefined;
	// English in a Chinese form is worse than no hint: it reads as a field
	// somebody forgot to finish. Fall back to the placeholder rather than to
	// the other language.
	const where = field ? pick(locale, [field.source_hint, field.source_hint_zh]) : undefined;
	const filled = point.value.trim() !== "";

	return (
		<div className={filled ? "datapoint is-filled" : "datapoint"}>
			<div className="datapoint-name">
				<strong>{label}</strong>
			</div>

			<div className="datapoint-value">
				<Value point={point} field={field} label={label} onChange={onChange} />
				{unit ? <span className="faint">{unit}</span> : null}
			</div>

			<label className="datapoint-source" htmlFor={`fact-source-${point.key}`}>
				<span className="hint">{t("settings.source")}</span>
				<Input
					id={`fact-source-${point.key}`}
					type="url"
					aria-label={`${label} ${t("settings.source")}`}
					// The schema knows where each fact is usually found. Saying so is
					// the difference between "source_url" and "the vendor's pricing
					// page", and it is the field people give up on.
					placeholder={where ?? "https://"}
					value={point.source_url}
					onChange={(e) => onChange({ source_url: e.target.value })}
					// Only when there is something to source. An empty row is not a
					// fact yet, and marking it red would make an untouched screen
					// look like a screen full of errors.
					aria-invalid={filled && !point.source_url.trim()}
				/>
			</label>
		</div>
	);
}

/** The control this fact's type deserves. */
function Value({
	label,
	point,
	field,
	onChange,
}: {
	label: string;
	point: Point;
	field?: DatapointField;
	onChange: (patch: Partial<Point>) => void;
}) {
	const t = useT();
	if (
		[
			"capabilities",
			"workflow",
			"integrations",
			"plan_limits",
			"trial_terms",
			"target_users",
			"free_tier_limit",
		].includes(point.key)
	) {
		return (
			<Textarea rows={4} aria-label={label} value={point.value} onChange={(e) => onChange({ value: e.target.value })} />
		);
	}

	if (field?.type === "boolean") {
		return (
			<NativeSelect aria-label={label} value={point.value} onChange={(e) => onChange({ value: e.target.value })}>
				<option value="">{t("settings.valueUnknown")}</option>
				<option value="true">{t("settings.valueYes")}</option>
				<option value="false">{t("settings.valueNo")}</option>
			</NativeSelect>
		);
	}
	if (field?.type === "enum" && field.values?.length) {
		return (
			<NativeSelect aria-label={label} value={point.value} onChange={(e) => onChange({ value: e.target.value })}>
				<option value="">{t("settings.valueUnknown")}</option>
				{field.values.map((value) => (
					<option key={value} value={value}>
						{value}
					</option>
				))}
			</NativeSelect>
		);
	}
	// The placeholder says what shape the answer takes. "Value" on a field that
	// wants a comma-separated list is the reason that field comes back with one
	// language in it.
	const placeholder =
		field?.type === "number"
			? t("settings.valueNumber")
			: field?.type === "array"
				? t("settings.valueList")
				: t("settings.value");
	return (
		<Input
			type={field?.type === "number" ? "number" : "text"}
			aria-label={label}
			placeholder={placeholder}
			value={point.value}
			onChange={(e) => onChange({ value: e.target.value })}
		/>
	);
}

/**
 * The engines, as one row of chips: no engine is another's regional variant.
 *
 * Shared with the wizard: the choice made on sign-up and the choice revised a
 * month later have to look like the same choice.
 */
export function EnginePicker({
	chosen,
	max,
	onToggle,
}: {
	chosen: string[];
	/** How many may be chosen; null is "no limit here": the wizard, before a plan. */
	max: number | null;
	onToggle: (key: string) => void;
}) {
	const t = useT();
	// A plan of zero engines covers none of them, so every chip is closed —
	// which is not the same as the wizard's "no limit yet".
	const full = max !== null && chosen.length >= max;
	return (
		<div className="wizard-chips engine-chips">
			{ENGINES.map((engine) => {
				const on = chosen.includes(engine.key);
				return (
					<ChoiceChip key={engine.key} selected={on} disabled={!on && full} onClick={() => onToggle(engine.key)}>
						{t(`engine.${engine.key}`)}
					</ChoiceChip>
				);
			})}
		</div>
	);
}

/**
 * Deleting the brand. Its own box at the foot of the form, in the one colour
 * this panel reserves for state that cannot be undone, and behind a dialog
 * that says so in words before the button that does it.
 */
function DangerZone({
	entityId,
	name,
	localeForErrors,
}: {
	entityId: string;
	name: string;
	localeForErrors: "en" | "zh";
}) {
	const t = useT();
	const [open, setOpen] = useState(false);
	const [busy, setBusy] = useState(false);

	async function remove() {
		setBusy(true);
		try {
			await api.deleteEntity(entityId);
			toast.success(t("settings.deleted"));
			// A whole-page load: the shell fetched the brand list before this
			// brand was gone, and the sidebar would otherwise keep offering it.
			window.location.assign("/");
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(localeForErrors) : String(e));
			setBusy(false);
			setOpen(false);
		}
	}

	return (
		<section className="danger-zone">
			<h2>{t("settings.danger")}</h2>
			<span className="hint">{t("settings.deleteHint")}</span>
			<Button type="button" variant="destructive" onClick={() => setOpen(true)}>
				{t("settings.delete")}
			</Button>
			<ChipConfirmModal
				open={open}
				onOpenChange={setOpen}
				title={t("settings.deleteTitle", { name })}
				text={t("settings.deleteBody")}
				dismissLabel={t("settings.deleteCancel")}
				confirm={{
					label: busy ? t("login.working") : t("settings.deleteConfirm"),
					variant: "destructive",
					onClick: remove,
					disabled: busy,
				}}
			/>
		</section>
	);
}

interface Point {
	key: string;
	value: string;
	unit: string;
	source_url: string;
}

interface StoredPoint {
	value?: string | number | boolean | null;
	unit?: string | null;
	source_url?: string | null;
}

/**
 * Every fact this brand could have, whether or not it has it.
 *
 * The union of what is stored and what the industry defines. A key the schema
 * dropped keeps its row — the value is still on the entity and dropping it
 * from the form would delete it on the next save — and a key with no value yet
 * gets an empty row, which is the only way anybody can add one: the server
 * refuses a key the schema does not name, so the schema *is* the list.
 *
 * Sorted filled-first, once, at load. Sorting on every keystroke would move a
 * row out from under the cursor the moment it was typed into.
 */
function toPoints(raw: unknown, schema?: Record<string, DatapointField>): Point[] {
	const stored = (raw && typeof raw === "object" ? raw : {}) as Record<string, StoredPoint>;
	const keys = [...new Set([...Object.keys(stored), ...Object.keys(schema ?? {})])];
	return keys
		.map((key) => {
			const point = stored[key];
			return {
				key,
				value: point?.value === null || point?.value === undefined ? "" : String(point.value),
				unit: point?.unit ?? "",
				source_url: point?.source_url ?? "",
			};
		})
		.sort((a, b) => Number(b.value !== "") - Number(a.value !== "") || a.key.localeCompare(b.key));
}

/**
 * Numbers go back as numbers.
 *
 * A price that arrives as the string "10" sorts and compares as text, so a
 * comparison table would put 9 above 10. The round trip through this form is
 * where that would happen silently.
 */
function fromPoints(points: Point[]): Record<string, unknown> {
	const out: Record<string, unknown> = {};
	for (const point of points) {
		if (!point.value.trim()) continue;
		const numeric = Number(point.value);
		out[point.key] = {
			value: point.value.trim() !== "" && Number.isFinite(numeric) ? numeric : point.value,
			...(point.unit ? { unit: point.unit } : {}),
			source_url: point.source_url,
		};
	}
	return out;
}

function replace(points: Point[], index: number, patch: Partial<Point>): Point[] {
	return points.map((point, i) => (i === index ? { ...point, ...patch } : point));
}

function lines(value: string): string[] {
	return value
		.split("\n")
		.map((line) => line.trim())
		.filter(Boolean);
}

async function compressLogo(file: File): Promise<string> {
	if (file.size > 5 * 1024 * 1024) throw new Error("image size");
	const bitmap = await createImageBitmap(file);
	try {
		const scale = Math.min(1, 64 / bitmap.width, 64 / bitmap.height);
		const canvas = document.createElement("canvas");
		canvas.width = Math.max(1, Math.round(bitmap.width * scale));
		canvas.height = Math.max(1, Math.round(bitmap.height * scale));
		const context = canvas.getContext("2d");
		if (!context) throw new Error("image decoding");
		context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
		return canvas.toDataURL("image/png");
	} finally {
		bitmap.close();
	}
}
